import useEmblaCarousel from 'embla-carousel-react';
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import { useCallback, useEffect, useRef } from 'react';

export type UseEmblaWithPluginsOptions = {
  /** Enable looping */
  loop?: boolean;
  /** Enable autoplay */
  autoplay?: boolean;
  /** Autoplay delay in ms */
  autoplayDelay?: number;
  /** Use fade effect instead of slide */
  fade?: boolean;
  /** Callback when selected index changes */
  onSelect?: (index: number) => void;
  /** Controlled index - carousel will scroll to this index when it changes */
  controlledIndex?: number;
  /** Whether carousel is enabled/active */
  enabled?: boolean;
};

/**
 * Shared Embla carousel hook with autoplay and fade plugin support.
 * Used by both PhotoCarousel and VenueCarousel for consistent behavior.
 */
export const useEmblaWithPlugins = ({
  loop = true,
  autoplay = false,
  autoplayDelay = 4000,
  fade = true,
  onSelect,
  controlledIndex,
  enabled = true,
}: UseEmblaWithPluginsOptions = {}) => {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const autoplayOptions: AutoplayOptionsType = {
    delay: autoplayDelay,
    stopOnInteraction: false,
    stopOnMouseEnter: false,
  };

  const plugins = [];
  if (autoplay && enabled) {
    plugins.push(Autoplay(autoplayOptions));
  }
  if (fade) {
    plugins.push(Fade());
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      watchDrag: enabled,
    },
    plugins,
  );

  // Handle select events
  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      onSelectRef.current?.(index);
    };

    emblaApi.on('select', handleSelect);
    // Fire initial select
    handleSelect();

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi]);

  // Handle controlled index changes
  useEffect(() => {
    if (!emblaApi || controlledIndex === undefined) return;

    const currentIndex = emblaApi.selectedScrollSnap();
    if (currentIndex !== controlledIndex) {
      emblaApi.scrollTo(controlledIndex);
    }
  }, [emblaApi, controlledIndex]);

  // Navigation helpers
  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    // Reset autoplay timer after manual navigation
    emblaApi.plugins()?.autoplay?.reset();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    emblaApi.plugins()?.autoplay?.reset();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      emblaApi.plugins()?.autoplay?.reset();
    },
    [emblaApi],
  );

  // Autoplay control
  const stopAutoplay = useCallback(() => {
    emblaApi?.plugins()?.autoplay?.stop();
  }, [emblaApi]);

  const startAutoplay = useCallback(() => {
    emblaApi?.plugins()?.autoplay?.play();
  }, [emblaApi]);

  return {
    emblaRef,
    emblaApi,
    scrollPrev,
    scrollNext,
    scrollTo,
    stopAutoplay,
    startAutoplay,
  };
};
